import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowRight, ArrowLeft, Building2, Palette, Settings, CheckCircle,
  Upload, X, Loader2, FileText, Mail, Phone, MapPin, Globe, Hash, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Business profile", icon: Building2 },
  { id: 2, title: "Branding", icon: Palette },
  { id: 3, title: "Defaults", icon: Settings },
  { id: 4, title: "Ready", icon: CheckCircle }
];

const CURRENCIES = [
  { code: "USD", label: "USD - US Dollar" },
  { code: "EUR", label: "EUR - Euro" },
  { code: "GBP", label: "GBP - British Pound" },
  { code: "CAD", label: "CAD - Canadian Dollar" },
  { code: "AUD", label: "AUD - Australian Dollar" },
  { code: "NGN", label: "NGN - Nigerian Naira" },
  { code: "KES", label: "KES - Kenyan Shilling" },
  { code: "ZAR", label: "ZAR - South African Rand" },
  { code: "INR", label: "INR - Indian Rupee" }
];

const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_7", label: "Net 7" },
  { value: "net_14", label: "Net 14" },
  { value: "net_30", label: "Net 30" }
];

const PAYMENT_METHODS = [
  { id: "cash", label: "Cash" },
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "mobile_money", label: "Mobile money" },
  { id: "card", label: "Card" }
];

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState({
    business_name: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    tax_id: "",
    logo_url: "",
    logo_data: "",
    primary_color: "#047857",
    default_currency: "USD",
    default_terms: "net_30",
    enabled_payment_methods: ["cash", "bank_transfer", "card"],
    default_tax_rate: 0,
    default_footer_note: ""
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setProfile(prev => ({ ...prev, ...response.data }));
      if (response.data.onboarding_step) {
        setCurrentStep(response.data.onboarding_step);
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (stepCompleted) => {
    setSaving(true);
    try {
      await axios.put(`${API}/business-profile`, {
        ...profile,
        onboarding_step: stepCompleted + 1
      });
    } catch (error) {
      console.error("Failed to save progress:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    if (currentStep === 1 && !profile.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }

    await saveProgress(currentStep);

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    navigate("/dashboard");
    toast.info("You can complete setup later from Settings");
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/business-profile/complete-onboarding`);
      updateUser({ onboarding_completed: true });
      toast.success("Setup complete!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      toast.error("Failed to complete setup");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setProfile(prev => ({ 
        ...prev, 
        logo_data: event.target?.result,
        logo_url: ""
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setProfile(prev => ({ ...prev, logo_data: "", logo_url: "" }));
  };

  const togglePaymentMethod = (methodId) => {
    setProfile(prev => {
      const methods = prev.enabled_payment_methods || [];
      if (methods.includes(methodId)) {
        return { ...prev, enabled_payment_methods: methods.filter(m => m !== methodId) };
      } else {
        return { ...prev, enabled_payment_methods: [...methods, methodId] };
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/ledgeramt-logo.png" 
              alt="LedgerAMT" 
              className="h-14 w-auto"
            />
          </div>
          
          {currentStep < 4 && (
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              Skip for now
            </Button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center gap-2 ${
                currentStep >= step.id ? "text-primary" : "text-muted-foreground"
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  currentStep > step.id 
                    ? "bg-primary text-white" 
                    : currentStep === step.id 
                    ? "bg-primary/10 text-primary border-2 border-primary" 
                    : "bg-muted text-muted-foreground"
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-12 sm:w-24 h-0.5 mx-2 ${
                  currentStep > step.id ? "bg-primary" : "bg-muted"
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
          {/* Step 1: Business Profile */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div>
                <h2 className="text-xl font-semibold mb-1">Business profile</h2>
                <p className="text-sm text-muted-foreground">
                  This information appears on your invoices and receipts.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Business name *</Label>
                  <Input
                    value={profile.business_name}
                    onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                    placeholder="Your business name"
                    className="mt-1.5"
                    data-testid="business-name-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Email</Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        placeholder="business@example.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        placeholder="+1 234 567 8900"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Address</Label>
                  <div className="relative mt-1.5">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Textarea
                      value={profile.address}
                      onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                      placeholder="123 Business St, City, Country"
                      className="pl-10 resize-none"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Website</Label>
                    <div className="relative mt-1.5">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={profile.website}
                        onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                        placeholder="https://example.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Tax ID</Label>
                    <div className="relative mt-1.5">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={profile.tax_id}
                        onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })}
                        placeholder="Tax/VAT number"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Branding */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div>
                <h2 className="text-xl font-semibold mb-1">Branding</h2>
                <p className="text-sm text-muted-foreground">
                  Add your logo and brand colors to personalize your documents.
                </p>
              </div>

              <div className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <Label className="text-sm">Logo</Label>
                  <div className="mt-2">
                    {profile.logo_data || profile.logo_url ? (
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-lg border border-border flex items-center justify-center overflow-hidden bg-muted/30">
                          <img 
                            src={profile.logo_data || profile.logo_url} 
                            alt="Logo" 
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="cursor-pointer">
                            <span className="text-sm text-primary hover:underline">Replace logo</span>
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden" 
                              onChange={handleLogoUpload}
                            />
                          </label>
                          <button 
                            onClick={removeLogo}
                            className="text-sm text-red-500 hover:underline text-left"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                        <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">Click to upload logo</span>
                        <span className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleLogoUpload}
                          data-testid="logo-upload"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Primary Color */}
                <div>
                  <Label className="text-sm">Primary color</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <input
                      type="color"
                      value={profile.primary_color}
                      onChange={(e) => setProfile({ ...profile, primary_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={profile.primary_color}
                      onChange={(e) => setProfile({ ...profile, primary_color: e.target.value })}
                      className="w-32"
                      placeholder="#047857"
                    />
                  </div>
                </div>

                {/* Footer Note */}
                <div>
                  <Label className="text-sm">Document footer text</Label>
                  <Textarea
                    value={profile.default_footer_note}
                    onChange={(e) => setProfile({ ...profile, default_footer_note: e.target.value })}
                    placeholder="Thank you for your business."
                    className="mt-1.5 resize-none"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Defaults */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div>
                <h2 className="text-xl font-semibold mb-1">Defaults</h2>
                <p className="text-sm text-muted-foreground">
                  Set default options for new invoices and documents.
                </p>
              </div>

              <div className="space-y-6">
                {/* Currency */}
                <div>
                  <Label className="text-sm">Default currency</Label>
                  <Select 
                    value={profile.default_currency} 
                    onValueChange={(v) => setProfile({ ...profile, default_currency: v })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(curr => (
                        <SelectItem key={curr.code} value={curr.code}>{curr.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Terms */}
                <div>
                  <Label className="text-sm">Payment terms</Label>
                  <Select 
                    value={profile.default_terms} 
                    onValueChange={(v) => setProfile({ ...profile, default_terms: v })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="terms-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(term => (
                        <SelectItem key={term.value} value={term.value}>{term.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Methods */}
                <div>
                  <Label className="text-sm">Payment methods</Label>
                  <div className="mt-2 space-y-2">
                    {PAYMENT_METHODS.map(method => (
                      <div key={method.id} className="flex items-center justify-between py-2">
                        <span className="text-sm">{method.label}</span>
                        <Switch
                          checked={profile.enabled_payment_methods?.includes(method.id)}
                          onCheckedChange={() => togglePaymentMethod(method.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Default Tax Rate */}
                <div>
                  <Label className="text-sm">Default tax rate (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={profile.default_tax_rate}
                    onChange={(e) => setProfile({ ...profile, default_tax_rate: parseFloat(e.target.value) || 0 })}
                    className="mt-1.5 w-32"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Ready */}
          {currentStep === 4 && (
            <div className="space-y-6 text-center" data-testid="step-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-1">You're all set!</h2>
                <p className="text-sm text-muted-foreground">
                  Your business profile is ready. Here's a summary:
                </p>
              </div>

              {/* Summary */}
              <div className="bg-muted/30 rounded-lg p-4 text-left max-w-sm mx-auto">
                <div className="flex items-center gap-3 mb-4">
                  {(profile.logo_data || profile.logo_url) ? (
                    <img 
                      src={profile.logo_data || profile.logo_url} 
                      alt="Logo" 
                      className="w-12 h-12 object-contain rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold">{profile.business_name || "Your Business"}</p>
                    <p className="text-xs text-muted-foreground">{profile.email}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Currency</span>
                    <span>{profile.default_currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Terms</span>
                    <span>{PAYMENT_TERMS.find(t => t.value === profile.default_terms)?.label}</span>
                  </div>
                </div>
              </div>

              <div className="text-sm text-muted-foreground space-y-1">
                <p>• Add clients as you create invoices</p>
                <p>• Issued invoices appear in Pending until paid</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <div>
              {currentStep > 1 && currentStep < 4 && (
                <Button variant="ghost" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {currentStep < 4 ? (
                <Button onClick={handleContinue} disabled={saving} data-testid="continue-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/dashboard/tools/invoice")}>
                    Create first invoice
                  </Button>
                  <Button onClick={handleComplete} disabled={saving} data-testid="finish-btn">
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Go to dashboard
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
